import React, { useEffect, useId, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import Box from '@mui/material/Box';
import { alpha } from '@mui/system';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { useNavigate, useParams } from 'react-router-dom';
import tokens from '@/nav/tokens.json';
import { employerRefApi } from '@/employer-ref/services/employerRefApi';
import SubmitButton from '@/components/Button/SubmitButton';
import webStorage from '@/core/web-storage/webStorage';
import { LOCAL_STORAGE_KEYS } from '@/core/web-storage/webStorage';
import SectionLoader from '@/components/Loader/SectionLoader';
import { formatEmployerLabel } from '@/nav/utils/navigation.utils';
import { ROUTE_NAMES } from '@/core/routes/routeNames';
import { getGlobalLocation } from '@/common/utils/app.utils';

interface EmployerOption {
  label: string;
  id: string;
}

type ChangeBoardDialogProps = {
  open: boolean;
  currentBoardId: string;
  onClose: () => void;
  showClose?: boolean;
};

function ChangeBoardDialog({
  open,
  currentBoardId,
  onClose,
  showClose = true,
}: ChangeBoardDialogProps) {
  const dialogId = useId();
  const params = useParams();
  const navigate = useNavigate();
  const [employerOptions, setEmployerOptions] = useState([] as EmployerOption[]);
  const [selectedEmployerOption, setSelectedEmployerOption] = useState<EmployerOption | null>(null);
  const {
    data: employersReferenceData,
    isLoading,
    isSuccess,
    isError,
    error,
  } = employerRefApi.useGetAllEmployerRefInfoQuery();

  useEffect(() => {
    if (employersReferenceData) {
      const selectedEmployer = employersReferenceData.find(
        (employer) => employer.boardNumber === currentBoardId,
      );
      if (selectedEmployer) {
        setSelectedEmployerOption({
          id: selectedEmployer.boardNumber,
          label: formatEmployerLabel(selectedEmployer.boardNumber, selectedEmployer.employerName),
        });
      }
      setEmployerOptions(
        employersReferenceData
          .map((employer) => {
            return {
              label: formatEmployerLabel(employer.boardNumber, employer.employerName),
              id: employer.boardNumber,
            } as EmployerOption;
          })
          .sort((employer1, employer2) => {
            if (employer1.id < employer2.id) {
              return -1;
            } else if (employer1.id > employer2.id) {
              return 1;
            } else {
              return 0;
            }
          }),
      );
    }
  }, [employersReferenceData]);

  function reloadOrGotoDashboard(isUnchanged: boolean) {
    if (!isUnchanged && 'employeeId' in params) {
      navigate(ROUTE_NAMES.DASHBOARD.DASHBOARD);
    } else {
      getGlobalLocation().reload();
    }
  }

  function onChangeBoard() {
    if (selectedEmployerOption) {
      const current = webStorage.local.getItem(LOCAL_STORAGE_KEYS.BOARD_ID);
      webStorage.local.setItem(LOCAL_STORAGE_KEYS.BOARD_ID, selectedEmployerOption.id);
      webStorage.local.setItem(LOCAL_STORAGE_KEYS.IS_USER_BOARD_SELECTED, 'true');
      onClose();
      reloadOrGotoDashboard(current === selectedEmployerOption.id);
    }
  }

  function getDialogDescription() {
    switch (true) {
      case isSuccess:
        return tokens['CHANGE_BOARD_DESCRIPTION'];
      case isError:
        return error as string;
      default:
        tokens['EMPLOYER_REFERENCE_NOT_FOUND_TRY_AGAIN'];
    }
  }

  return (
    <Dialog
      fullWidth
      open={open}
      aria-describedby={dialogId}
      disableEscapeKeyDown
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: '0px' } }}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar sx={{ gap: 2, pl: 2, pr: 4 }} disableGutters>
          {showClose ? (
            <IconButton
              size="large"
              color="inherit"
              aria-label="Close"
              role="button"
              onClick={onClose}
            >
              <CloseIcon />
            </IconButton>
          ) : null}
          <Typography
            id={dialogId}
            data-testid="change-board-title"
            pl={2}
            flex={1}
            component="h5"
            sx={{ pl: 2, flex: 1, fontSize: '1rem' }}
            variant="h5"
            fontWeight="600"
          >
            {tokens['CHANGE_BOARD_TITLE']}
          </Typography>
          {isSuccess ? (
            <SubmitButton
              role="button"
              aria-label="Change board"
              title={
                selectedEmployerOption
                  ? 'Change board'
                  : 'Please select an employer to change board'
              }
              disabled={!selectedEmployerOption}
              onClick={onChangeBoard}
              sx={{
                '&.Mui-disabled': {
                  pointerEvents: 'auto',
                },
              }}
            >
              Ok
            </SubmitButton>
          ) : null}
        </Toolbar>
      </AppBar>
      {isLoading ? (
        <Box p={8} alignItems="center">
          <SectionLoader />
        </Box>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          gap={6}
          alignItems="center"
          sx={(theme) => ({
            backgroundColor: alpha(theme.palette.primary.main, 0.04),
            padding: theme.spacing(6, 12, 12, 6),
          })}
        >
          <Typography sx={(theme) => ({ color: theme.palette.custom.primaryDark })} variant="body1">
            {getDialogDescription()}
          </Typography>
          {isSuccess ? (
            <Autocomplete
              sx={{ width: '100%' }}
              options={employerOptions}
              value={selectedEmployerOption as EmployerOption}
              disableClearable
              renderInput={(params) => (
                <TextField {...params} label={tokens['CHANGE_BOARD_TITLE']} />
              )}
              onChange={(_, value) => {
                if (value) {
                  setSelectedEmployerOption(value);
                }
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id || value === null}
            />
          ) : null}
        </Box>
      )}
    </Dialog>
  );
}

export default ChangeBoardDialog;
